import { useState, useEffect } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { adminAPI } from '../../utils/api';

const fmt = (secs) => {
  if (!secs) return '0m';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const StatCard = ({ icon, val, label, color, bg }) => (
  <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
    <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>{icon}</div>
    <div>
      <div style={{ fontSize: '22px', fontWeight: 900, color, lineHeight: 1 }}>{val}</div>
      <div style={{ fontSize: '10px', color: '#64748B', fontWeight: 600, marginTop: '2px' }}>{label}</div>
    </div>
  </div>
);

const Toggle = ({ value, onChange, label, desc }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '14px 0', borderBottom: '1px solid #F1F5F9' }}>
    <div>
      <div style={{ fontSize: '13px', fontWeight: 700 }}>{label}</div>
      {desc && <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '2px' }}>{desc}</div>}
    </div>
    <div onClick={onChange} style={{ width: '40px', height: '22px', borderRadius: '11px', background: value ? '#6B4EFF' : '#E2E8F0', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0, marginLeft: '16px' }}>
      <div style={{ position: 'absolute', top: '3px', left: value ? '21px' : '3px', width: '16px', height: '16px', borderRadius: '50%', background: 'white', transition: 'left 0.2s' }}></div>
    </div>
  </div>
);

const AdminAnalyticsPage = () => {
  const [view, setView] = useState('platform'); // platform | school | student
  const [platformData, setPlatformData] = useState(null);
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [schoolData, setSchoolData] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [notifSettings, setNotifSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadPlatform(); }, []);

  const loadPlatform = async () => {
    setLoading(true);
    try {
      const [pRes, sRes, nRes] = await Promise.all([
        adminAPI.getPlatformAnalytics(),
        adminAPI.getSchools(),
        adminAPI.getNotificationSettings()
      ]);
      setPlatformData(pRes.data.data);
      setSchools(sRes.data.data || []);
      setNotifSettings(nRes.data.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadSchool = async (school) => {
    setSelectedSchool(school);
    setView('school');
    setLoading(true);
    try {
      const res = await adminAPI.getSchoolAnalytics(school.id);
      setSchoolData(res.data.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadStudent = async (student) => {
    setSelectedStudent(student);
    setView('student');
    setLoading(true);
    try {
      const res = await adminAPI.getStudentAnalytics(student.id);
      setStudentData(res.data.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const saveNotif = async () => {
    setSaving(true);
    try {
      await adminAPI.updateNotificationSettings(notifSettings);
      alert('Saved!');
    } catch { alert('Failed.'); }
    finally { setSaving(false); }
  };

  // Breadcrumb
  const Breadcrumb = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', fontSize: '13px' }}>
      <span onClick={() => { setView('platform'); setSelectedSchool(null); setSelectedStudent(null); }}
        style={{ color: '#6B4EFF', cursor: 'pointer', fontWeight: 700 }}>Platform</span>
      {selectedSchool && <>
        <span style={{ color: '#CBD5E1' }}>›</span>
        <span onClick={() => { setView('school'); setSelectedStudent(null); }}
          style={{ color: view === 'school' ? '#1A1A2E' : '#6B4EFF', cursor: view === 'school' ? 'default' : 'pointer', fontWeight: 700 }}>{selectedSchool.name}</span>
      </>}
      {selectedStudent && <>
        <span style={{ color: '#CBD5E1' }}>›</span>
        <span style={{ color: '#1A1A2E', fontWeight: 700 }}>{selectedStudent.name}</span>
      </>}
    </div>
  );

  const tabs = view === 'platform' ? ['overview', 'schools', 'subjects', 'notifications'] : [];

  return (
    <AdminShell title="Analytics" subtitle="Drill down from platform → school → student">
      <Breadcrumb />

      {view === 'platform' && (
        <>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: '#F7F8FC', borderRadius: '10px', padding: '4px', width: 'fit-content' }}>
            {tabs.map(t => (
              <button key={t} onClick={() => setActiveTab(t)}
                style={{ padding: '7px 16px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: 700, cursor: 'pointer', textTransform: 'capitalize', background: activeTab === t ? 'white' : 'transparent', color: activeTab === t ? '#6B4EFF' : '#64748B', boxShadow: activeTab === t ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
                {t}
              </button>
            ))}
          </div>

          {loading ? <div style={{ textAlign: 'center', padding: '60px', color: '#64748B' }}>Loading...</div> : (
            <>
              {activeTab === 'overview' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                    <StatCard icon="👥" val={platformData?.today?.active_users || 0} label="Active Today" color="#6B4EFF" bg="#F3F0FF" />
                    <StatCard icon="⏱️" val={fmt(platformData?.today?.total_seconds)} label="Study Time Today" color="#1A3C6E" bg="#E8F0FB" />
                    <StatCard icon="📖" val={platformData?.today?.topics_studied || 0} label="Topics Today" color="#1B7F3E" bg="#E6F4EC" />
                    <StatCard icon="✏️" val={platformData?.today?.quizzes_taken || 0} label="Quizzes Today" color="#B45309" bg="#FEF3C7" />
                  </div>

                  {/* Weekly chart */}
                  <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '16px' }}>Weekly Active Users</div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '120px' }}>
                      {platformData?.weekly_users?.length > 0 ? platformData.weekly_users.map((d, i) => {
                        const max = Math.max(...platformData.weekly_users.map(x => x.users));
                        const h = max > 0 ? Math.round((d.users / max) * 90) : 4;
                        return (
                          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <div style={{ fontSize: '10px', color: '#64748B', fontWeight: 700 }}>{d.users}</div>
                            <div style={{ width: '100%', height: `${h}px`, background: '#6B4EFF', borderRadius: '4px', minHeight: '4px', opacity: 0.7 + (i * 0.04) }}></div>
                            <div style={{ fontSize: '9px', color: '#94A3B8' }}>{new Date(d.activity_date).toLocaleDateString('en', { weekday: 'short' })}</div>
                          </div>
                        );
                      }) : <div style={{ color: '#94A3B8', fontSize: '13px', margin: 'auto' }}>No activity data yet</div>}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'schools' && (
                <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden' }}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E8F0', fontSize: '13px', fontWeight: 800 }}>
                    All Schools — Click to drill down
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#F7F8FC' }}>
                        {['School', 'Status', 'Students', 'Active Today', 'Avg Study Time', ''].map(h => (
                          <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '10px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {schools.length === 0 ? (
                        <tr><td colSpan={6} style={{ textAlign: 'center', padding: '32px', color: '#64748B' }}>No schools registered yet</td></tr>
                      ) : schools.map((s, i) => (
                        <tr key={s.id} style={{ borderBottom: i < schools.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ fontSize: '13px', fontWeight: 700 }}>{s.name}</div>
                            <div style={{ fontSize: '11px', color: '#64748B' }}>{s.city} · CBSE #{s.cbse_affiliation_no}</div>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <span style={{ fontSize: '10px', fontWeight: 700, padding: '3px 8px', borderRadius: '20px', background: s.status === 'approved' ? '#E6F4EC' : '#FEF3C7', color: s.status === 'approved' ? '#1B7F3E' : '#B45309' }}>
                              {s.status}
                            </span>
                          </td>
                          <td style={{ padding: '12px 14px', fontSize: '13px', fontWeight: 700 }}>—</td>
                          <td style={{ padding: '12px 14px', fontSize: '13px' }}>—</td>
                          <td style={{ padding: '12px 14px', fontSize: '13px' }}>—</td>
                          <td style={{ padding: '12px 14px' }}>
                            {s.status === 'approved' && (
                              <button onClick={() => loadSchool(s)}
                                style={{ padding: '5px 12px', borderRadius: '6px', background: '#F3F0FF', color: '#6B4EFF', border: 'none', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
                                View Details →
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === 'subjects' && (
                <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '14px' }}>Subject Popularity Platform-wide</div>
                  {platformData?.top_subjects?.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {platformData.top_subjects.map((s, i) => {
                        const max = Math.max(...platformData.top_subjects.map(x => x.total_seconds));
                        const pct = max > 0 ? Math.round((s.total_seconds / max) * 100) : 0;
                        return (
                          <div key={i}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                              <span style={{ fontSize: '13px', fontWeight: 700, textTransform: 'capitalize' }}>{s.subject}</span>
                              <span style={{ fontSize: '12px', color: '#64748B' }}>{fmt(s.total_seconds)} · {s.users} students</span>
                            </div>
                            <div style={{ background: '#F1F5F9', borderRadius: '10px', height: '8px' }}>
                              <div style={{ background: '#6B4EFF', borderRadius: '10px', height: '100%', width: `${pct}%`, transition: 'width 0.5s' }}></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : <div style={{ color: '#94A3B8', fontSize: '13px' }}>No subject data yet</div>}
                </div>
              )}

              {activeTab === 'notifications' && notifSettings && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '4px' }}>Student Notifications</div>
                    <div style={{ fontSize: '11px', color: '#94A3B8', marginBottom: '8px' }}>Control what alerts students receive</div>
                    <Toggle value={notifSettings.student_inactivity_enabled} onChange={() => setNotifSettings(p => ({ ...p, student_inactivity_enabled: !p.student_inactivity_enabled }))} label="Inactivity Reminder" desc="Alert student if not logged in for 3+ days" />
                    <Toggle value={notifSettings.student_revision_reminder} onChange={() => setNotifSettings(p => ({ ...p, student_revision_reminder: !p.student_revision_reminder }))} label="Revision Due Reminder" desc="Remind student when Day 1/4/7 revision is due" />
                    <Toggle value={notifSettings.student_goal_reminder} onChange={() => setNotifSettings(p => ({ ...p, student_goal_reminder: !p.student_goal_reminder }))} label="Daily Goal Reminder" desc="Nudge student to meet their daily study goal" />
                    <Toggle value={notifSettings.student_achievement_notify} onChange={() => setNotifSettings(p => ({ ...p, student_achievement_notify: !p.student_achievement_notify }))} label="Achievement Unlocked" desc="Notify when student earns a badge or milestone" />
                    <Toggle value={notifSettings.student_weekly_report} onChange={() => setNotifSettings(p => ({ ...p, student_weekly_report: !p.student_weekly_report }))} label="Weekly Progress Report" desc="Send weekly summary of topics and quizzes" />
                  </div>

                  <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '4px' }}>School Admin Notifications</div>
                    <div style={{ fontSize: '11px', color: '#94A3B8', marginBottom: '8px' }}>Control what alerts school admins receive</div>
                    <Toggle value={notifSettings.school_inactive_student_alert} onChange={() => setNotifSettings(p => ({ ...p, school_inactive_student_alert: !p.school_inactive_student_alert }))} label="Inactive Student Alert" desc="Alert school if a student hasn't studied in 3+ days" />
                    <Toggle value={notifSettings.school_low_score_alert} onChange={() => setNotifSettings(p => ({ ...p, school_low_score_alert: !p.school_low_score_alert }))} label="Low Score Alert" desc={`Alert school if student scores below ${notifSettings.school_low_score_threshold}%`} />
                    <Toggle value={notifSettings.school_weekly_report} onChange={() => setNotifSettings(p => ({ ...p, school_weekly_report: !p.school_weekly_report }))} label="Weekly School Report" desc="Send weekly performance summary to school admin" />
                    <Toggle value={notifSettings.school_new_student_notify} onChange={() => setNotifSettings(p => ({ ...p, school_new_student_notify: !p.school_new_student_notify }))} label="New Student Registered" desc="Notify when a new student joins the school" />
                    <Toggle value={notifSettings.school_pending_approval_notify} onChange={() => setNotifSettings(p => ({ ...p, school_pending_approval_notify: !p.school_pending_approval_notify }))} label="Pending Approval Reminder" desc="Remind school admin of students awaiting approval" />
                  </div>

                  <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '4px' }}>Notification Channels</div>
                    <div style={{ fontSize: '11px', color: '#94A3B8', marginBottom: '8px' }}>Choose how notifications are delivered</div>
                    <Toggle value={notifSettings.channel_inapp} onChange={() => setNotifSettings(p => ({ ...p, channel_inapp: !p.channel_inapp }))} label="In-App Notifications" desc="Always on — shown inside the platform" />
                    <Toggle value={notifSettings.channel_email} onChange={() => setNotifSettings(p => ({ ...p, channel_email: !p.channel_email }))} label="Email Notifications" desc="Send notifications via email" />
                    <Toggle value={notifSettings.channel_whatsapp} onChange={() => setNotifSettings(p => ({ ...p, channel_whatsapp: !p.channel_whatsapp }))} label="WhatsApp Notifications" desc="Future feature — requires WhatsApp Business API" />
                  </div>

                  <button onClick={saveNotif} disabled={saving}
                    style={{ background: '#6B4EFF', color: 'white', border: 'none', borderRadius: '10px', padding: '12px 24px', fontSize: '13px', fontWeight: 800, cursor: 'pointer', width: 'fit-content', opacity: saving ? 0.7 : 1 }}>
                    {saving ? 'Saving...' : '✓ Save Settings'}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* School Drill-down View */}
      {view === 'school' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {loading ? <div style={{ textAlign: 'center', padding: '60px', color: '#64748B' }}>Loading school data...</div> : (
            <>
              {/* School stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                <StatCard icon="👥" val={schoolData?.today_active || 0} label="Active Today" color="#6B4EFF" bg="#F3F0FF" />
                <StatCard icon="⏱️" val={fmt(schoolData?.avg_daily_seconds)} label="Avg Daily Study" color="#1A3C6E" bg="#E8F0FB" />
                <StatCard icon="⚠️" val={schoolData?.at_risk_students?.length || 0} label="Students At Risk" color="#DC2626" bg="#FEE2E2" />
                <StatCard icon="📊" val={schoolData?.class_activity?.length || 0} label="Active Classes" color="#1B7F3E" bg="#E6F4EC" />
              </div>

              {/* Class breakdown */}
              <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
                <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '14px' }}>Class-wise Activity (Last 7 Days)</div>
                {schoolData?.class_activity?.length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' }}>
                    {schoolData.class_activity.map((c, i) => (
                      <div key={i} style={{ background: '#F7F8FC', borderRadius: '10px', padding: '14px', textAlign: 'center', cursor: 'pointer', border: '1px solid #E2E8F0' }}>
                        <div style={{ fontSize: '20px', fontWeight: 900, color: '#6B4EFF' }}>Class {c.class_id}</div>
                        <div style={{ fontSize: '12px', fontWeight: 700, marginTop: '4px' }}>{c.active_students} active</div>
                        <div style={{ fontSize: '11px', color: '#64748B', marginTop: '2px' }}>{fmt(c.avg_seconds)}/day avg</div>
                      </div>
                    ))}
                  </div>
                ) : <div style={{ color: '#94A3B8', fontSize: '13px' }}>No class activity yet</div>}
              </div>

              {/* Subject breakdown */}
              <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
                <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '14px' }}>Subject Engagement</div>
                {schoolData?.top_subjects?.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {schoolData.top_subjects.map((s, i) => {
                      const max = Math.max(...schoolData.top_subjects.map(x => x.total_seconds));
                      const pct = max > 0 ? Math.round((s.total_seconds / max) * 100) : 0;
                      return (
                        <div key={i}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ fontSize: '13px', fontWeight: 700, textTransform: 'capitalize' }}>{s.subject}</span>
                            <span style={{ fontSize: '12px', color: '#64748B' }}>{fmt(s.total_seconds)} · {s.student_count} students</span>
                          </div>
                          <div style={{ background: '#F1F5F9', borderRadius: '10px', height: '8px' }}>
                            <div style={{ background: '#6B4EFF', borderRadius: '10px', height: '100%', width: `${pct}%` }}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : <div style={{ color: '#94A3B8', fontSize: '13px' }}>No subject data yet</div>}
              </div>

              {/* At risk students */}
              {schoolData?.at_risk_students?.length > 0 && (
                <div style={{ background: 'white', border: '1px solid #FEE2E2', borderRadius: '12px', padding: '20px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 800, color: '#DC2626', marginBottom: '14px' }}>⚠️ Students At Risk — Not studied in 3+ days</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#FFF5F5' }}>
                        {['Student', 'Class', 'Last Active', 'Action'].map(h => (
                          <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: '10px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {schoolData.at_risk_students.map((s, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
                          <td style={{ padding: '10px 12px', fontSize: '13px', fontWeight: 700 }}>{s.name}</td>
                          <td style={{ padding: '10px 12px', fontSize: '12px' }}>Class {s.class_id}</td>
                          <td style={{ padding: '10px 12px', fontSize: '12px', color: '#DC2626' }}>
                            {s.last_active ? new Date(s.last_active).toLocaleDateString('en-IN') : 'Never'}
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <button onClick={() => loadStudent(s)}
                              style={{ padding: '4px 10px', borderRadius: '6px', background: '#F3F0FF', color: '#6B4EFF', border: 'none', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
                              View Profile →
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* All students table */}
              <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E8F0', fontSize: '13px', fontWeight: 800 }}>All Students — Click to view profile</div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#F7F8FC' }}>
                      {['Student', 'Class', 'Status', 'Last Active', ''].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '10px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {schoolData?.students?.length > 0 ? schoolData.students.map((s, i) => (
                      <tr key={s.id} style={{ borderBottom: i < schoolData.students.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                        <td style={{ padding: '10px 14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#F3F0FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800, color: '#6B4EFF' }}>
                              {s.name?.charAt(0).toUpperCase()}
                            </div>
                            <div style={{ fontSize: '13px', fontWeight: 700 }}>{s.name}</div>
                          </div>
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: '12px' }}>Class {s.class_id}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ fontSize: '10px', fontWeight: 700, padding: '3px 8px', borderRadius: '20px', background: s.status === 'active' ? '#E6F4EC' : '#FEF3C7', color: s.status === 'active' ? '#1B7F3E' : '#B45309' }}>
                            {s.status}
                          </span>
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: '12px', color: '#64748B' }}>—</td>
                        <td style={{ padding: '10px 14px' }}>
                          <button onClick={() => loadStudent(s)}
                            style={{ padding: '4px 10px', borderRadius: '6px', background: '#F3F0FF', color: '#6B4EFF', border: 'none', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
                            View →
                          </button>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan={5} style={{ textAlign: 'center', padding: '32px', color: '#64748B' }}>No students yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Student Drill-down View */}
      {view === 'student' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {loading ? <div style={{ textAlign: 'center', padding: '60px', color: '#64748B' }}>Loading student data...</div> : (
            <>
              {/* Student header */}
              <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: '#F3F0FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: 800, color: '#6B4EFF' }}>
                  {selectedStudent?.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 800 }}>{selectedStudent?.name}</div>
                  <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>Class {selectedStudent?.class_id} · {selectedSchool?.name}</div>
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '20px', background: selectedStudent?.status === 'active' ? '#E6F4EC' : '#FEF3C7', color: selectedStudent?.status === 'active' ? '#1B7F3E' : '#B45309' }}>
                    {selectedStudent?.status}
                  </span>
                </div>
              </div>

              {/* Student stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                <StatCard icon="⏱️" val={fmt(studentData?.total_seconds)} label="Total Study Time" color="#6B4EFF" bg="#F3F0FF" />
                <StatCard icon="📅" val={studentData?.study_days || 0} label="Days Studied" color="#1A3C6E" bg="#E8F0FB" />
                <StatCard icon="🕐" val={studentData?.peak_hour !== undefined ? `${studentData.peak_hour}:00` : '—'} label="Peak Study Hour" color="#1B7F3E" bg="#E6F4EC" />
                <StatCard icon="📖" val={studentData?.subject_breakdown?.length || 0} label="Subjects Active" color="#B45309" bg="#FEF3C7" />
              </div>

              {/* Subject breakdown */}
              <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
                <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '14px' }}>Time Spent Per Subject</div>
                {studentData?.subject_breakdown?.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {studentData.subject_breakdown.map((s, i) => {
                      const max = Math.max(...studentData.subject_breakdown.map(x => x.total_seconds));
                      const pct = max > 0 ? Math.round((s.total_seconds / max) * 100) : 0;
                      return (
                        <div key={i}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ fontSize: '13px', fontWeight: 700, textTransform: 'capitalize' }}>{s.subject}</span>
                            <span style={{ fontSize: '12px', color: '#64748B' }}>{fmt(s.total_seconds)}</span>
                          </div>
                          <div style={{ background: '#F1F5F9', borderRadius: '10px', height: '8px' }}>
                            <div style={{ background: '#6B4EFF', borderRadius: '10px', height: '100%', width: `${pct}%` }}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : <div style={{ color: '#94A3B8', fontSize: '13px' }}>No study data yet for this student</div>}
              </div>

              {/* Last 7 days activity */}
              <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
                <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '16px' }}>Last 7 Days Activity</div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', height: '100px' }}>
                  {studentData?.week_activity?.length > 0 ? studentData.week_activity.map((d, i) => {
                    const max = Math.max(...studentData.week_activity.map(x => x.total_seconds));
                    const h = max > 0 ? Math.round((d.total_seconds / max) * 80) : 4;
                    return (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                        <div style={{ fontSize: '10px', color: '#64748B', fontWeight: 700 }}>{fmt(d.total_seconds)}</div>
                        <div style={{ width: '100%', height: `${h}px`, background: '#6B4EFF', borderRadius: '4px', minHeight: '4px', opacity: 0.8 }}></div>
                        <div style={{ fontSize: '9px', color: '#94A3B8' }}>{new Date(d.activity_date).toLocaleDateString('en', { weekday: 'short' })}</div>
                      </div>
                    );
                  }) : <div style={{ color: '#94A3B8', fontSize: '13px', margin: 'auto' }}>No activity this week</div>}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </AdminShell>
  );
};

export default AdminAnalyticsPage;
